import React, { forwardRef, memo, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { MediaItem } from '../../stores/reducers/media';
import Icon, { COLORS, ICONS } from '../general-components/icon';
import { getVideoTimeAsHHMMSS } from '../../utils/utils';
import { useAudioSink } from '../../hooks/devices/use-audio-sink';
import { CreateMediaFromGenerateContentSound } from '../../connection/media'
import IconToggle from './icon-toggle';
import useShots from '../../hooks/use-shots';
import SoundVersionManager from '../../utils/sound/sound-manager';
import { useEffectiveShotDuration } from '../../hooks/use-effective-shot-duration';

interface ChromiumAudioContext extends AudioContext {
	setSinkId(sinkId: string): Promise<void>;
}

/*
	This shim extension exists to simply currentTime / duration reflection.
	- offsetStart and offsetEnd are meant to offset a piece of static media.
		- We could offset media via MediaSourceBuffers, but that feature is not current supported in some safari instances.
			So I have opted to fake it via currentTime, duration, tiemchange events, etc.
		- AdvanceHTMLAudioElement will override native functions / properties of an audio element/object
			to offset all values relative to the offsetStart/offsetEnd
		- fadeIn / fadeOut is achieved through modifying the incoming audio signal (via a gainNode)
			vs modifying the outgoing signal (volume)
		- to deal with the echo node, there is a "mirror" property, that will listen to events from
			another node to mimic things that are happening to it (such as fadeIn/Out setting)
			- Note, the echo (audio) node has no real duration and currentTime is always set to 0
				after a play/pause

	When Debugging, this is the timeline and where 0 is represented for each internal object

	AudioContext							|--------------------------------------------------------------------------|
	Media(this) Loaded/Existing					|--offsetStart--|---------------------------------|--offsetEnd--|
	AudioContext.currentTime				0
	Media(this).currentTime										0
	Media(super).currentTime					0
*/
class AdvancedHTMLAudioElement extends HTMLAudioElement {
	private _offsetStart: number = 0;
	private _offsetEnd: number = 0;
	private _fadeIn: [number, number] = [0, 0];
	private _fadeOut: [number, number] = [0, 0];
	private _audioContext?: AudioContext;
	private _gainNode?: GainNode;
	private _fadeInTimingSet: boolean = false;
	private _fadeOutTimingSet: boolean = false;
	private _repeatOffset: number = 0; //This is needed because the echo element that it needs to mirror, always as duration -> infinity
	private _sinkId: string = '';
	  
	constructor() {
		super();
		this.createContext();

		this.addEventListener("timeupdate", this.handleTimeUpdate.bind(this));
		this.addEventListener("loadedmetadata", this.handleLoadedMetaData.bind(this));
		this.addEventListener("play", this.handlePlay.bind(this));
		this.addEventListener("pause", this.handlePause.bind(this));
	}

	createContext(stream?: MediaStream) {
		this._audioContext = new AudioContext();
		this._gainNode = new GainNode(this._audioContext, { gain: 1 });

		if (stream) {
			const source = this._audioContext.createMediaStreamSource(stream);
			const dest = this._audioContext.createMediaStreamDestination();

			source.connect(this._gainNode);
			this._gainNode.connect(dest);

			return dest;
		} else {
			const source = this._audioContext.createMediaElementSource(this);
			source.connect(this._gainNode);
			this._gainNode.connect(this._audioContext.destination);

			return this._audioContext.destination;
		}
	}

	needFadeInEffect() {
		return (this.fadeIn[1] - this.fadeIn[0] > 0) //if there is a fadeIn configured
			|| (this.fadeOut[0]) //if there is a fadeOut, we need to reset to gain = 1
			;
	}

	setAttribute(name: string, value: string): void {
		switch (name) {
			case 'autoplay':
				this.autoplay = value.toLowerCase() == 'true';
				break;
			case 'controls':
				this.controls = value.toLowerCase() == 'true';
				break;
			case 'loop':
				this.loop = value.toLowerCase() == 'true';
				break;
			case 'muted':
				this.muted = value.toLowerCase() == 'true';
				break;
			default:
				super.setAttribute(name, value);
				break;
		}
	}

	get sinkId(): string {
		return this._sinkId;
	}

	set sinkId(value: string) {
		if (value && this._sinkId != value) {		    
			this._sinkId = value;
			if (this._audioContext && this._sinkId) {
				try {
					(this._audioContext as ChromiumAudioContext).setSinkId(this._sinkId);
				} catch (error) {
					console.error('Failed to set sinkId:', error);
				}
			}
		}
	}

	// Override or add explicit getters and setters
	get fadeIn(): [number, number] {
		return [this._fadeIn[0], this._fadeIn[1]];
	}
	set fadeIn(value: [number, number]) {
		if (value && isFinite(value[0]) && isFinite(value[1])) {
			this._fadeIn = value;
			this._fadeInTimingSet = false;
		}
	}

	get fadeOut(): [number, number] {
		return [this._fadeOut[0], this._fadeOut[1]];
	}
	set fadeOut(value: [number, number]) {
		if (value && isFinite(value[0]) && isFinite(value[1])) {
			this._fadeOut = value;
			this._fadeOutTimingSet = false;
		}
	}

	get offsetStart(): number {
		return this._offsetStart;
	}
	set offsetStart(value: number) {
		if (isFinite(value)) {
			let time = this.currentTime;
			this._offsetStart = value;
			this.currentTime = time;

			//this.duration is valid once loadedmetadata / loadeddata has triggered
			//	so use it to "know" that the event has happened at least once
			//	re-trigger it because that is the normal expectation when duration is changed
			if (this.duration && time != super.currentTime) {
				this.dispatchEvent(new Event("loadedmetadata"));
				this.dispatchEvent(new Event("loadeddata"));
			}
		}
	}

	get offsetEnd(): number {
		return this._offsetEnd;
	}
	set offsetEnd(value: number) {
		if (isFinite(value)) {
			let time = this.duration;
			this._offsetEnd = value;

			//this.duration is valid once loadedmetadata / loadeddata has triggered
			//	so use it to "know" that the event has happened at least once
			//	re-trigger it because that is the normal expectation when duration is changed
			if (this.duration && time != super.duration) {
				this.dispatchEvent(new Event("loadedmetadata"));
				this.dispatchEvent(new Event("loadeddata"));
			}
		}
	}

	get currentTime(): number {
		return super.currentTime - this._offsetStart;
	}
	set currentTime(value: number) {
		if (isFinite(value)) {
			const now = this.currentTime;
			value += this._offsetStart;

			if (value == now)
				return;

			//this is a reset to beginning
			if (value == this._offsetStart && now != 0) {
				this._fadeOutTimingSet = false;

				const event = new CustomEvent("reset", {
					detail: { message: "Reset triggered!", duration: this.duration },
				});
				this.dispatchEvent(event);
			}

			super.currentTime = value;
		}
	}

	get volume(): number {
		return super.volume;
	}
	set volume(value: number) {
		if (isFinite(value)) {
			super.volume = value;
		}
	}

	private _mirrorTarget: any;
	private _lastMirrorTargetBind: any;

	set mirror(value: any) {
		if (value == this._mirrorTarget)
			return;

		if (value) {
			this._mirrorTarget = value;
			this._lastMirrorTargetBind = this.onMirrorEvents.bind(this);
			this._mirrorTarget.addEventListener("fadeset", this._lastMirrorTargetBind);
			this._mirrorTarget.addEventListener("reset", this._lastMirrorTargetBind);
		} else {
			if (this._mirrorTarget) {
				this._mirrorTarget.removeEventListener("fadeset", this._lastMirrorTargetBind);
				this._mirrorTarget.removeEventListener("reset", this._lastMirrorTargetBind);
				this._mirrorTarget = null;
				this._lastMirrorTargetBind = null;
			}
		}
	}

	private onMirrorEvents(event: any) {
		switch (event.type) {
			case "fadeset":
				{
					const offset = this.currentTime - this._mirrorTarget.currentTime;
					switch (event.detail.fadeType) {
						case "in":
							this.fadeIn = [event.detail.time[0] + offset, event.detail.time[1] + offset];
							break;
						case "out":
							this.fadeOut = [event.detail.time[0] + offset, event.detail.time[1] + offset];
							break;
					}
				}
				break;
			case "reset":
				{
					this._repeatOffset += event.detail.duration;
				}
				break;
		}
	}

	get duration(): number {
		return super.duration - this._offsetStart - this._offsetEnd;
	}

	get isInFadeIn(): boolean {
		return this.currentTime >= this.fadeIn[0] && this.currentTime <= this.fadeIn[1];
	}
	get isInFadeOut(): boolean {
		let timeStart = this.fadeOut[0];
		let timeEnd = this.fadeOut[1];
		if (this.fadeOut[0] < 0) {
			timeStart += this.duration;
			timeEnd += this.duration;
		}
		return this.currentTime >= timeStart && this.currentTime <= timeEnd;
	}

	private setFadeInEffect() {
		this._fadeInTimingSet = true;

		if (this._audioContext && this._gainNode) {
			if (this.currentTime <= this.fadeIn[1] && this.needFadeInEffect()) {

				const needsScheduling = this.fadeIn[1] - this.currentTime >= 0;
				/*
				NOTE, setValueCurveAtTime is relative to when the AudioContext was CREATED.
					NOT relative to the time of the MEDIA
				*/

				let timeStart = this.fadeIn[0] + this._audioContext.currentTime - this.currentTime;
				let timeEnd = this.fadeIn[1] + this._audioContext.currentTime - this.currentTime;
				let gainStart = 0;
				const gainEnd = 1;
				let duration = this.fadeIn[1] - this.fadeIn[0];

				if (timeStart < 0) {
					/*
						Since timeStart cannot be negative for setValueCurveAtTime,
							Calcuate the proper % of gain if timeStart was at 0
					*/
					gainStart = -1.0 * timeStart / (timeEnd - timeStart) * (gainEnd - gainStart);
					let newTimeStart = this._audioContext.currentTime;

					const diffBetweenStarts = newTimeStart - timeStart;
					duration = duration - diffBetweenStarts;
					timeStart = newTimeStart;
				}
				timeStart = Math.floor(timeStart);

				if (needsScheduling) {
					this._gainNode.gain.cancelScheduledValues(this._audioContext.currentTime);

					if (!duration)
						this._gainNode.gain.linearRampToValueAtTime(gainEnd, timeStart);
					else
						this._gainNode.gain.setValueCurveAtTime([gainStart, gainEnd], timeStart, duration);

				}
				const event = new CustomEvent("fadeset", {
					detail: {
						message: "Fade In Set",
						fadeType: "in",
						gain: [gainStart, gainEnd],
						time: [this.fadeIn[0], this.fadeIn[1]]
					},
				});
				this.dispatchEvent(event);
			}

		}
	}

	private setFadeOutEffect() {
		this._fadeOutTimingSet = true;

		const hasFadeOut = this.fadeOut[0] && this.fadeOut[1] - this.fadeOut[0] > 0;
		if (this._audioContext && this._gainNode && hasFadeOut) {
			let relative = this.fadeOut[0] < 0;

			let timeStart = this.fadeOut[0];
			if (relative && isFinite(this.duration))
				timeStart += this.duration;

			/*
			NOTE, setValueCurveAtTime is relative to when the AudioContext was CREATED.
				NOT relative to the time of the MEDIA
			*/
			let timeEnd = this.fadeOut[1];
			if (relative && isFinite(this.duration))
				timeEnd += this.duration;
			let offset = this._audioContext.currentTime - this.currentTime;
			if (isFinite(this.duration))
				offset += this._repeatOffset;

			timeStart += offset;
			timeEnd += offset;
			let gainStart = 1;
			const gainEnd = 0;
			let duration = this.fadeOut[1] - this.fadeOut[0];

			if (timeStart < 0) {
				/*
					Since timeStart cannot be negative for setValueCurveAtTime,
						Calcuate the proper % of gain if timeStart was at 0
				*/
				gainStart = -1.0 * timeStart / (timeEnd - timeStart) * (gainStart - gainEnd);
				const newTimeStart = this._audioContext.currentTime;
				duration = timeEnd - timeStart;

				const diffBetweenStarts = newTimeStart - timeStart;
				duration = duration - diffBetweenStarts;
				timeStart = newTimeStart;
			}
			timeStart = Math.floor(timeStart);

			const needsScheduling = this.currentTime <= timeStart;
			if (needsScheduling) {
				this._gainNode.gain.cancelScheduledValues(this._audioContext.currentTime);
				this._gainNode.gain.setValueCurveAtTime([gainStart, gainEnd], timeStart, duration);
			}

			timeStart = this.fadeOut[0];
			if (relative && isFinite(this.duration))
				timeStart += this.duration;
			timeEnd = this.fadeOut[1];
			if (relative && isFinite(this.duration))
				timeEnd += this.duration;
			const event = new CustomEvent("fadeset", {
				detail: {
					message: "Fade Out Set",
					fadeType: "out",
					gain: [gainStart, gainEnd],
					time: [timeStart, timeEnd]
				},
			});
			this.dispatchEvent(event);
		}
	}

	//custom default handlers
	private handlePlay(event: Event) {
		if (isFinite(super.duration)) {
			this.setFadeInEffect();
		}
		else if (this._mirrorTarget) {
			this._repeatOffset = 0;

			const offset = this.currentTime - this._mirrorTarget.currentTime;
			if (this._mirrorTarget.isInFadeIn) {
				const fade = this._mirrorTarget.fadeIn;
				if (fade[1]) {
					fade[0] += offset;
					fade[1] += offset;
					this.fadeIn = fade;
					this.setFadeInEffect();
				}
			}
			else if (this._mirrorTarget.isInFadeOut) {
				const fade = this._mirrorTarget.fadeOut;
				if (fade[0]) {
					if (fade[0] < 0) {
						fade[0] += this._mirrorTarget.duration;
						fade[1] += this._mirrorTarget.duration;
					}
					fade[0] += offset;
					fade[1] += offset;
					this.fadeOut = fade;
					this.setFadeOutEffect();
				}
			}
		}

		// Propagate the event to any external listener
		this.dispatchDefaultEvent(event);
	}

	private handleTimeUpdate(event: Event) {
		const adjustedTime = this.currentTime; // Adjusted for offset
		let triggerEarlyEnd = this._offsetEnd && (adjustedTime >= this.duration);

		if (!this._fadeInTimingSet && this.currentTime >= this.fadeIn[0]) {
			this.setFadeInEffect();
		} else if (!this._fadeOutTimingSet && this.currentTime > this.fadeIn[1]) {
			this.setFadeOutEffect();
		}

		// Propagate the event to any external listener
		this.dispatchDefaultEvent(event);

		if (triggerEarlyEnd) {
			if (this.loop) {
				this.currentTime = 0;
				this.setFadeInEffect();
			}
			else {
				this.pause();
				const endedEvent = new Event("ended");
				this.dispatchEvent(endedEvent);
			}

			this._fadeOutTimingSet = false;
		}
	}

	private handlePause(event: Event) {
		//There is no duration / Infinity on the echo cancel audio elems, reset the fade effects
		//	because when playback starts again, the way the audio feeds into here, time is back to 0
		if (!isFinite(super.duration)) {
			this.fadeIn = [0, 0];
			this.fadeOut = [0, 0];
		}
	}

	private handleLoadedMetaData(event: Event) {
		this._repeatOffset = 0;

		this.dispatchDefaultEvent(event);
	}

	private dispatchDefaultEvent(event: Event) {
		// Propagate the event to any external listener
		const externalHandler = this.onloadedmetadata as EventListener | null;
		if (externalHandler) {
			externalHandler.call(this, event);
		}
	}

	// Override the play method
	async play(): Promise<void> {
		// Call the original play method
		const result = await super.play();

		return result;
	}
}

// Registering as a custom element (required to extend built-ins)
customElements.define("advanced-audio", AdvancedHTMLAudioElement, { extends: "audio" });



interface MediaAudioProps {
	id: string;
	mediaItem: MediaItem | undefined;
	duration?: number;
	elapsed: number;
	loop?: boolean;
	muted?: boolean;
	shownVolume?: number | null;
	onEnded?: () => void;
	onError?: (error: any) => void;
	onPlay?: () => void;
	onLoadStart?: () => void;
	onTimeUpdate?: () => void;
	onLoadedData?: () => void;
	specialEffects?: boolean;
	mirror?: HTMLMediaElement;
	showVersions?: boolean;
	soundManager?: SoundVersionManager;
	displayOnly?: boolean;
	externalPaused?: boolean;
	externalCurrentTime?: number;
	externalDuration?: number;
	onDisplayPlayPause?: () => void;
	onDisplayScrubStart?: () => void;
	onDisplayScrub?: (time: number) => void;
	onDisplayScrubEnd?: (time: number) => void;
	onDisplayVersionSelect?: () => void;
}

const MediaAudio = forwardRef((
	{
		id,
		mediaItem,
		elapsed,
		loop,
		muted,
		shownVolume = 1,
		onEnded,
		onError,
		onPlay,
		onLoadStart,
		onTimeUpdate,
		onLoadedData,
		specialEffects = true,
		mirror,
		showVersions = false,
		displayOnly = false,
		externalPaused,
		externalCurrentTime,
		externalDuration,
		onDisplayPlayPause,
		onDisplayScrubStart,
		onDisplayScrub,
		onDisplayScrubEnd,
		onDisplayVersionSelect,
	}: MediaAudioProps,
	ref: any,
) => {
	if (!mediaItem) {
		return (
			<></>
		);
	}
	const [currentTime, setCurrentTime] = useState(0);
	const [duration, setDuration] = useState(0);
	const { sinkId } = useAudioSink();
	const token = useSelector((s: any) => s.SessionReducer.token);
	const meetingId = useSelector((s: any) => s.SessionReducer.meeting?.meeting_id);
	const [original, setOriginal] = useState<any>({});
	const [versions, setVersions] = useState([]);
	const [versionsLoading, setVersionsLoading] = useState(false);
	const [showLoopableVersions, setShowLoopableVersions] = useState(false);
	const [showVersionsPanel, setShowVersionsPanel] = useState(false);
	const [selectedVersion, setSelectedVersion] = useState<any>({ audio: mediaItem.images });
	const [thumbnailData, setThumbnailData] = useState<string | null>(null);
	const clickRef = useRef<HTMLDivElement>(null);
	
	const hasVersions = showVersions && mediaItem.provider === 'epidemic sound' && mediaItem.template_type;
	
	const { currentShot } = useShots();
	const currentShotDuration = useEffectiveShotDuration(currentShot);
	const soundManager = SoundVersionManager.getInstance();

	useEffect(() => {
		if (ref?.current) {
			ref.current.offsetStart = specialEffects ? mediaItem.media_data.offsetStartSecond : 0;
		}
	}, [specialEffects, mediaItem.media_data.offsetStartSecond]);
	useEffect(() => {
		if (ref?.current) {
			ref.current.offsetEnd = specialEffects ? mediaItem.media_data.offsetEndSecond : 0;
		}
	}, [specialEffects, mediaItem.media_data.offsetEndSecond]);
	useEffect(() => {
		if (ref?.current) {
			ref.current.fadeIn = specialEffects ? [0, mediaItem.media_data.fadeIn] : [0, 0];
		}
	}, [specialEffects, mediaItem.media_data.fadeIn]);
	useEffect(() => {
		if (ref?.current) {
			ref.current.fadeOut = specialEffects ? [-1 * (mediaItem.media_data.fadeOut ?? 0), 0] : [0, 0];
		}
	}, [specialEffects, mediaItem.media_data.fadeOut]);
	useEffect(() => {
		if (ref?.current) {
			ref.current.volume = specialEffects ? shownVolume : 1;
		}
	}, [shownVolume]);
	useEffect(() => {
		if (ref?.current) {
			ref.current.mirror = specialEffects ? mirror : null;
		}
	}, [specialEffects, mirror]);
	useEffect(() => {
		if (ref?.current && sinkId) {
			ref.current.sinkId = sinkId;
		}
	}, [sinkId]);
	
	useEffect(() => {
		if (ref?.current) {
			if (ref.current.src !== mediaItem.images) {
				ref.current.src = mediaItem.images;
				ref.current.load();
			}
		}

		setSelectedVersion({ audio: mediaItem.images });
	}, [mediaItem.images]);

	useEffect(() => {
		if (hasVersions) {			
			fetchEpidemicVersion(mediaItem);
		} else {
			setVersions([]);
		}
	}, [hasVersions, showVersions, mediaItem.id, mediaItem.provider, mediaItem.template_type, currentShotDuration]);

	// Preload thumbnail image and convert to data
	useEffect(() => {
		if (mediaItem.thumbnail_url) {			
			fetch(mediaItem.thumbnail_url)
				.then(async (response) => {
					const bytes = await response.arrayBuffer();
										
					const uint8Array = new Uint8Array(bytes);
					let binaryString = '';
					for (let i = 0; i < uint8Array.length; i++) {
						binaryString += String.fromCharCode(uint8Array[i]);
					}
					const base64 = btoa(binaryString);
					const mimeType = response.headers.get('content-type') || 'image/jpeg';
					const data = `data:${mimeType};base64,${base64}`;
					if (thumbnailData !== data)
						setThumbnailData(data);
				})
				.catch(error => {
					console.error('Error fetching thumbnail:', error);
					setThumbnailData(null);
				});
		}
	}, [mediaItem.thumbnail_url, thumbnailData]);

	// Close versions panel when clicking outside
	useEffect(() => {
	const handleClickOutside = (event: MouseEvent) => {
		if (clickRef.current && !clickRef.current.contains(event.target as Node)) {
			setShowVersionsPanel(false);
		}
	};

	if (showVersionsPanel) {
		document.addEventListener('mousedown', handleClickOutside);
		
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}
	
	// No cleanup needed if listener wasn't added
	return undefined;
}, [showVersionsPanel]);

	const effectiveCurrentTime = displayOnly ? (externalCurrentTime ?? 0) : currentTime;
	const effectiveDuration = displayOnly ? (externalDuration ?? 0) : duration;
	const effectivePaused = displayOnly ? (externalPaused ?? true) : ref?.current?.paused;
	const remainingTime = effectiveDuration - effectiveCurrentTime;
	const radius = 8;
	
	const playButtonWidth = radius * 11.5 + radius * 2; // 108px (button width + margin)
	const versionsButtonWidth = hasVersions ? 24 + radius * 1.5 + radius * 2 : 0; // 52px when visible
	const totalButtonsWidth = playButtonWidth + versionsButtonWidth;

	const getAudioMimeType = (type: string) => {
		switch (type) {
			case 'aac':
				return 'audio/aac';
			case 'mp3':
				return 'audio/mpeg';
			case 'wav':
				return 'audio/wav';
			default:
				return '';
		}
	};

	function onPlayPauseClick(e: React.MouseEvent<HTMLElement>) {
		e.stopPropagation();
		if (displayOnly) {
			onDisplayPlayPause?.();
			return;
		}
		if (ref?.current) {
			if (ref.current.paused)
				ref.current.play();
			else
				ref.current.pause();
		}
	}

	function onTimeChange() {
		if (displayOnly) return;
		if (ref?.current) {
			setCurrentTime(ref.current.currentTime);
		}

		if (onTimeUpdate)
			onTimeUpdate();

		if (currentTime > duration) {
			ref?.current.pause();
			if (onEnded)
				onEnded();
		}

	}

	function onLoadedMetadata() {
		if (ref?.current) {
			setDuration(ref.current.duration);
		}
	}

	function onTimeSliderChange(e: React.ChangeEvent<HTMLInputElement>) {
		if (displayOnly) {
			onDisplayScrub?.(Number(e.target.value));
			return;
		}
		if (ref?.current)
			ref.current.currentTime = Number(e.target.value);
	}

	async function handleVersionClick(version: any) {
		if (!version.audio) return;
		if (!displayOnly && ref?.current && ref.current.src != version.audio) {
			ref.current.src = version.audio;
			ref.current.load();

			try {
				await ref.current.play();
			} catch (error) {
			}
		}

		setSelectedVersion(version);

		// Create media from the selected version
		if (mediaItem && mediaItem.id && token && meetingId && original.id && version.id) {
			await CreateMediaFromGenerateContentSound(
				token,
				meetingId,
				original.id,
				version.id,
				mediaItem?.id
			);
		}

		if (displayOnly) {
			onDisplayVersionSelect?.();
		}
	}

	function handleSetShowVersionsPanel(e: React.MouseEvent<HTMLElement>) {
		e.stopPropagation();
		if (!showVersionsPanel && mediaItem) {
			const version: any = versions.find((version: any) => version.audio === mediaItem.images);
			if (version) setShowLoopableVersions(!!version.loopable);
		}
		setShowVersionsPanel(!showVersionsPanel);
	}

	async function fetchEpidemicVersion (mediaItem: MediaItem) {
		try {
			setVersionsLoading(true);
			setVersions([]);

			let data:any = mediaItem.media_data;
			let currentShotDurationSeconds = data.original?.config?.duration ?? currentShotDuration ?? 30;
			
			await soundManager.fetchVersions(
				token,
				mediaItem.template_type || '',
				currentShotDurationSeconds,
				{
					onProgress: (data: any) => {
						if (data.audio && data.audio !== original.audio)
							setOriginal(data);
					},
					onComplete: (data: any) => {
						setOriginal(data);
						if (data.versions) {
							let actual = data.versions.reduce((sum: number, x: any) => sum + Number(x.duration), 0) / data.versions.length;
							let target = data.versions.reduce((sum: number, x: any) => sum + Number(x.targetDuration), 0) / data.versions.length;
							if (1.0 * actual / target < 0.9)
								setShowLoopableVersions(true);
              
							setVersions(data.versions);
							setVersionsLoading(false);
						}
					}
				}
			);
		} catch (error) {
			console.error('Error fetching epidemic sound version:', error);
			setVersionsLoading(false);
		}
	}
	
	return (
		<div ref={clickRef}>
			<div 
				className={`audio-player ${ showVersionsPanel ? 'background-grayscale-1200' : 'background-grayscale-800' } `}
				data-has-versions={hasVersions ? '1' : '0'}
				{...(hasVersions && { onClick: handleSetShowVersionsPanel })}
			>
				<button
					className='background-grayscale-600'
					style={{
						width: `${radius * 11.5}px`,
						height: `${radius * 8}px`,
						borderRadius: `${radius}px`,
						flexShrink: '0',
						marginRight: `${radius * 2}px`,
						...(thumbnailData && {
							backgroundImage: `url(${thumbnailData})`,
							backgroundSize: 'cover',
							backgroundPosition: 'center',
							maskImage: 'linear-gradient(to right, rgba(9,9,10,1) 0%, rgba(9,9,10,0) 100%)',
							maskRepeat: 'no-repeat',
							maskSize: 'cover',
							WebkitMaskImage: 'linear-gradient(to right, rgba(9,9,10,1) 0%, rgba(9,9,10,0) 100%)',
							WebkitMaskRepeat: 'no-repeat',
							WebkitMaskSize: 'cover'
						}),
					}}
					onClick={onPlayPauseClick}
				>
					<Icon
						name={effectivePaused ? ICONS.PLAY : ICONS.PAUSE}
						size={24}
						color={COLORS.TEXT_GRAY_100} />
				</button>
				<div className='audio-header-info' style={{ width: `calc(100% - ${totalButtonsWidth}px)`, marginRight: '12px' }}>
					<div className='audio-info'>
						<label style={{
							minWidth: '0',
							overflow: 'hidden',
							textOverflow: 'ellipsis'
						}}>{mediaItem.original_name}</label>
						<span style={{
							flexGrow: 1,
							minWidth: `${radius}px`,
						}}></span>
						{versionsLoading ? (
							<label>Loading</label>
						) : (
							<label>-{getVideoTimeAsHHMMSS(remainingTime)}</label>
						)}
					</div>
					<div className="slider-input">
					{versionsLoading ? (
						<div className='slider-loading'></div>
					) : (
						<input
							style={{ margin: '0' }}
							onChange={onTimeSliderChange}
							onMouseDown={displayOnly ? () => onDisplayScrubStart?.() : undefined}
							onMouseUp={displayOnly ? (e) => onDisplayScrubEnd?.(Number((e.target as HTMLInputElement).value)) : undefined}
							type="range"
							min="0"
							max={effectiveDuration}
							value={effectiveCurrentTime}
							onClick={(e) => e.stopPropagation()}
						/>
					)}
					</div>
				</div>
				{hasVersions && (
				<button
					style={{
						width: '24px',
						height: '24px',
						background: 'transparent',
						marginLeft: `${radius * 1.5}px`,
						marginRight: `${radius * 2}px`,
					}}
					onClick={handleSetShowVersionsPanel}
				>
					<Icon
						name={showVersionsPanel? ICONS.CARROT_UP : ICONS.CARROT_DOWN}
						size={16}
						color={COLORS.TEXT_GRAY_100} />
				</button>
				)}
			</div>
			{!displayOnly && (
			<audio
				id={id}
				ref={ref}
				is="advanced-audio"
				className="media-audio"
				onPlay={onPlay}
				onEnded={onEnded}
				onError={(error) => {
					if (onError) {
						onError(error);
					}
				}}
				muted={muted}
				loop={loop}
				controls={true}
				onLoadStart={onLoadStart}
				onTimeUpdate={onTimeChange}
				onLoadedData={onLoadedData}
				onLoadedMetadata={onLoadedMetadata}
				crossOrigin={'anonymous'}
			>
				<source
					src={mediaItem.images}
					type={getAudioMimeType(mediaItem?.type ?? '')}
				/>
			</audio>
		)}

			{showVersionsPanel && (
				<div className="versions-floating-parent">
					<div className="versions-header">
						<div style={{display: 'flex', flexDirection: 'column'}}>
							<div style={{flexGrow: 1}}></div>
							<Icon name={ICONS.SPARKLE_AI} color={COLORS.TEXT_GRAY_100} size={11.4}></Icon>
							<div style={{flexGrow: 1}}></div>
						</div>
						<div style={{
							marginLeft: '8px',
							flexGrow: 1,
							display: 'flex',
							flexDirection: 'column'
						}} >
							<div style={{flexGrow: 1}}></div>
							<span className='versions-hint'>Generated track options</span>
							<div style={{flexGrow: 1}}></div>
						</div>
						<IconToggle
							onIcon={ICONS.LOOP_TRACK_ON}
							offIcon={ICONS.LOOP_TRACK_OFF}
							size={16}
							checked={showLoopableVersions}
							onChange={setShowLoopableVersions}
						/>
					</div>

					{!versions || !versions.length && (
						<div className="versions-container" data-waiting="1">
							{Array.from({ length: 5 }, (_, index) => (
								<div key={index} className="version-item">
									<div className='version-header'>
										<label className="version-title" aria-hidden="true">ABCDEFGH</label>
										<span style={{flexGrow: 1}}></span>
										<label className="version-time">00:00</label>
									</div>
									<div className="version-waveform-container">
										<div></div>
										<div></div>
									</div>
								</div>
							))}
						</div>
					)}
					{versions && versions.length > 0 && (
						<div className="versions-container" data-waiting="0">
							{versions
								.filter((version: any) => showLoopableVersions == version.loopable)
								.map((version: any) => (
									<div 
										key={version.id} 
										className="version-item" 
										data-selected={selectedVersion?.audio === version.audio ? "1" : "0"}
										onClick={() => handleVersionClick(version)}
									>
										<div className="version-header">
											{selectedVersion?.audio === version.audio && (
											<span style={{marginRight: '4px'}}>
												<Icon
													name={ICONS.CHECK_CIRCLE}
													size={16}
													color={COLORS.LEMONADE_PINK} />
											</span>)}
											<label className="version-title" style={{flexGrow: 1}}>{version.title}</label>
											<label className="version-time">{version.duration ? getVideoTimeAsHHMMSS(version.duration) : ''}</label>
										</div>
										{version.waveform && (
											<div className="version-waveform-container">
												<div className='version-waveform-mask' style={{
													WebkitMaskImage: `url(${version.waveform})`,
													maskImage: `url(${version.waveform})`,
												}}>
													<div className="version-waveform-color"></div>
												</div>
											</div>
										)}
										{version.audio && (
											<audio
												loop={version.loopable}
												crossOrigin={'anonymous'}
												onLoadedMetadata={(e) => {
													const audioElement = e.target as HTMLAudioElement;
													version.duration = audioElement.duration;
												}}
											>
												<source src={version.audio} />
											</audio>
										)}
									</div>
								))
							}
						</div>
					)}
					{original && (
						<div className="versions-container" data-waiting="0" data-original="1">
							<div 
								className="version-item" 
								data-selected={selectedVersion?.audio === original.audio ? "1" : "0"}
								onClick={() => handleVersionClick(original)}
							>
								<div className="version-header">
									{selectedVersion?.audio === original.audio && (
									<span style={{marginRight: '4px'}}>
										<Icon
											name={ICONS.CHECK_CIRCLE}
											size={16}
											color={COLORS.LEMONADE_PINK} />
									</span>)}
									<label className="version-title" style={{flexGrow: 1}}>Original</label>
									<label className="version-time">{original.duration ? getVideoTimeAsHHMMSS(original.duration) : ''}</label>
								</div>
								{original.waveform && (
									<div className="version-waveform-container">
										<div className='version-waveform-mask' style={{
											WebkitMaskImage: `url(${original.waveform})`,
											maskImage: `url(${original.waveform})`,
										}}>
											<div className="version-waveform-color"></div>
										</div>
									</div>
								)}
								{original.audio && (
									<audio
										loop={original.loopable}
										crossOrigin={'anonymous'}
										onLoadedMetadata={(e) => {
											const audioElement = e.target as HTMLAudioElement;
											original.duration = audioElement.duration;
										}}
									>
										<source src={original.audio} />
									</audio>
								)}
							</div>
						</div>
					)}
				</div>
			)}
		</div>
	);
});

MediaAudio.displayName = 'MediaAudio';

export default memo(MediaAudio);
